import { useQuery } from '@tanstack/react-query';
import { Link, useRouterState } from '@tanstack/react-router';
import { ShieldCheck, ListChecks } from 'lucide-react';
import { api, throwIfNotOk } from '../../lib/api';
import { qk } from '../../lib/queryKeys';

/**
 * SIL-302 (W4, spec slice 1 "Setup Guide") — header chip linking back to
 * `/financials/setup`. Same query key as the setup page itself
 * (`client-financials-setup`) so completing a step there refetches this too
 * without a second round trip on navigation.
 *
 * Hidden on `/financials/setup` itself (redundant with the page's own
 * progress readout) and while loading/erroring (best-effort — never a
 * fatal element in the header).
 */
export function SetupProgressChip() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const setupQ = useQuery({
    queryKey: qk.financials.setup(),
    queryFn: async () => {
      const res = await api.api.client.financials.setup.$get();
      await throwIfNotOk(res);
      return res.json();
    },
    staleTime: 30_000,
  });

  if (pathname === '/financials/setup') return null;
  if (setupQ.isLoading || setupQ.error || !setupQ.data) return null;

  const { completedCount, totalSteps } = setupQ.data;
  const complete = completedCount >= totalSteps;

  return (
    <Link
      to="/financials/setup"
      title="Financials setup guide"
      className="mr-1 inline-flex shrink-0 items-center gap-1.5 rounded-full border border-zinc-200 px-2 py-1 text-xs font-medium text-zinc-600 hover:bg-zinc-50 sm:px-2.5"
    >
      {complete ? (
        <ShieldCheck className="size-3.5 text-emerald-600" />
      ) : (
        <ListChecks className="size-3.5 text-brand-600" />
      )}
      {/* SIL: label text hidden below sm — the header's right-hand control
          cluster doesn't shrink/wrap otherwise and overflows the page at
          mobile widths (390px). Icon + border alone still identifies the
          control and it's still a tap target into the full setup guide. */}
      <span className="hidden sm:inline">
        Setup {completedCount}/{totalSteps}
      </span>
    </Link>
  );
}
